/**
 * Meetings and incentives grid - Figma `Block/M&I` (`6377:86988`, and `6377:87430`,
 * `6377:87464`; on the home page `3883:70467`).
 *
 * **Rows of two, the panel as tall as the photograph beside it.** The designer's rule as
 * of 2026-09-26, and what `6377:87430` draws: an orange panel and a photograph, both
 * 670x670. It replaced two offset columns - a 672 square beside a 399 panel, from the home
 * page's older frame (3883:70467) - which this used to reproduce. A grid row is as tall as
 * its tallest cell, which is now exactly the point: the photograph is square, and the
 * panel stretches to meet it. A panel with more text than a square holds makes the row
 * taller and the photograph fills it.
 *
 * Which tile is which is decided by the content: a card with an image or a video is the
 * photograph, one without is the panel. The editor controls the alternation by ordering
 * the list.
 *
 * Field mapping: `title` is the panel heading, `body` the paragraph, `url` the button,
 * and `label` the chip ("Incentives", "Meetings") over a photo or above the text.
 *
 * **The photo tiles play video now.** The design has always drawn a play button on them;
 * `videoUrl` on the card is what it needed. A tile with only an image is still a still.
 *
 * One thing the design shows that is not built: **the panel button is white on orange.**
 * The component set defines exactly two button types, blue-filled and blue-outlined, and
 * neither works on an orange panel. It is styled locally here rather than added to
 * Button, so the design system does not quietly grow a third variant nobody signed off.
 */
import type React from 'react';
import { Eyebrow, SectionShell, withHighlight } from '../primitives';
import { Icon } from '../../components/ui/Icon';
import type { Card, MeetingsGridSection } from '../../types/sections';
import { Image, SIZES } from '../../components/ui/Image';
import { VideoEmbed } from '../../components/ui/VideoEmbed';
import { AppLink } from '../../components/ui/AppLink';
import { useSite } from '../../lib/site-context';

/**
 * Fills the grid area it is given, so the row spans decide its height and nothing here
 * does. It used to be `aspect-square self-stretch`, which Safari resolves differently from
 * Chrome inside a grid: the photograph kept its own height and left a hole beside the panel.
 * On a phone, where there are no row spans, it takes a 4:3 frame instead.
 */
function PhotoTile({ card }: { card: Card }) {
  const chip = card.label ? (
    <figcaption className="pointer-events-none absolute inset-x-0 bottom-5 flex justify-center">
      <span className="rounded-full bg-black/50 px-6 py-2 text-caption text-white backdrop-blur-[7px]">{card.label}</span>
    </figcaption>
  ) : null;

  return (
    <figure className="relative m-0 aspect-[4/3] overflow-hidden rounded-l bg-surface-sunken lg:aspect-auto lg:h-full">
      {card.videoUrl ? (
        <VideoEmbed url={card.videoUrl} poster={card.image} alt={card.title} title={card.title} sizes={SIZES.half} />
      ) : card.image ? (
        <Image
          media={card.image}
          alt={card.image.alternativeText ?? card.title ?? ''}
          sizes={SIZES.half}
          className="absolute inset-0 h-full w-full object-cover"
        />
      ) : null}
      {chip}
    </figure>
  );
}

/**
 * The title at the top at h3, and the chip, the body and the button pinned to the foot
 * (6377:87430): the chip 12px, the body body-medium - 18px, the designer's call over the
 * file's 16.
 */
function PanelTile({ card }: { card: Card }) {
  const { t } = useSite();
  // Black on the orange, not white. Sampling the file's own render of this panel
  // (3883:70463) gives #000000 for the text against #ff5c00 for the ground; white on that
  // orange is 3.10:1, which fails AA for body text, and was never what the design drew.
  // `stats` already sets `text-ink-strong` on the same tile.
  return (
    <div className="flex h-full flex-col justify-between gap-10 rounded-l bg-orange-500 p-9 text-ink-strong">
      <h3 className="text-h3">{card.title}</h3>

      <div className="mt-auto flex flex-col gap-6">
        {card.label ? (
          <span className="inline-flex w-fit rounded-full bg-surface px-6 py-2 text-caption text-ink">{card.label}</span>
        ) : null}
        {card.body ? <p className="text-body-md">{card.body}</p> : null}
        {card.url ? (
          <AppLink
            to={card.url}
            className="group inline-flex w-fit items-center gap-2 rounded-m bg-surface px-8 py-3 text-body text-ink transition hover:bg-grey-200"
          >
            {t('cta.check')}
            <Icon name="arrow-right" size={18} className="transition group-hover:translate-x-1" />
          </AppLink>
        ) : null}
      </div>
    </div>
  );
}

const isPhoto = (card: Card) => Boolean(card.image || card.videoUrl);

/** A column is five units tall: a photograph takes three and a panel two, unless the editor says. */
const defaultUnits = (card: Card) => (isPhoto(card) ? 3 : 2);
const unitsOf = (card: Card) => Math.min(8, Math.max(1, Math.round(card.heightUnits ?? defaultUnits(card))));

/**
 * Cards deal alternately into two columns, and each is placed by hand on one grid: column
 * from its position, first row from what is already above it in that column, height from
 * its units. Two flex columns could not do this - they would not stay level with each other.
 */
function place(cards: Card[]) {
  const used = [0, 0];
  const placed = cards.map((card, i) => {
    const col = i % 2;
    const span = unitsOf(card);
    const row = used[col] + 1;
    used[col] += span;
    return { card, col, row, span };
  });
  return { placed, rows: Math.max(used[0], used[1], 1) };
}

export default function MeetingsGrid({ data }: { data: MeetingsGridSection }) {
  const cards = data.cards ?? [];
  const { placed, rows } = place(cards);

  return (
    <SectionShell settings={data.settings} name="meetings-grid">
      {data.eyebrow ? <Eyebrow value={data.eyebrow} /> : null}
      {data.heading ? <h2 className="max-w-4xl text-h3 text-ink lg:text-h2">{withHighlight(data.heading)}</h2> : null}
      {/* In the model since the landing page asked for it, and never drawn until 2026-09-26:
          an editor filled it in and saw nothing happen. */}
      {data.lead ? <p className="max-w-[640px] whitespace-pre-line text-body-md text-ink-muted">{data.lead}</p> : null}

      {cards.length ? (
        /* `cqw` is the container's width: a unit is a fifth of a column less its gaps, which
           makes five units as tall as a column is wide, the square the design draws. A panel
           with more text than its units hold grows its row, and the photographs follow. */
        <div className="mt-4 [container-type:inline-size]">
          <div
            className="grid gap-4 lg:grid-cols-2 lg:[grid-template-rows:var(--rows)]"
            style={{
              '--rows': `repeat(${rows}, minmax(calc(((100cqw - 16px) / 2 - 64px) / 5), auto))`,
            } as React.CSSProperties}
          >
            {placed.map(({ card, col, row, span }, i) => (
              <div
                key={i}
                className={`min-w-0 lg:[grid-row:var(--row)_/_span_var(--span)] ${col ? 'lg:col-start-2' : 'lg:col-start-1'}`}
                style={{ '--row': row, '--span': span } as React.CSSProperties}
              >
                {isPhoto(card) ? <PhotoTile card={card} /> : <PanelTile card={card} />}
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </SectionShell>
  );
}
